/**
 * `/j/$slug` — the waiting room, and the only screen in this app that exists
 * because of `docs/integration/REFERENCE-APP-HANDOFF.md`.
 *
 * What makes it different from `/` is what is NOT happening while you look at
 * it. On the home screen, pressing the button creates an ERA Stream room
 * immediately. Here, everyone who arrives before the host sits in a state this
 * application owns entirely: no session, no capacity unit, no recorder, no
 * token. The room appears when the host — and only the host — starts it.
 *
 * Two audiences, one route, decided by a capability rather than by a URL:
 *
 *   HOST   this tab holds a host credential — either the per-appointment token
 *          stashed when the link was created, or a session obtained with the
 *          shared host password. Gets a start button.
 *   GUEST  everybody else. Gets a waiting state and, once the meeting is live,
 *          is released into it automatically.
 *
 * There IS an "I'm the host" control, and it is not a contradiction: clicking
 * it does not make anyone a host, it asks for a password that the server
 * checks. Host-ness remains something the server verifies — 32 random bytes or
 * a signed session — never a claim the client makes about itself. The button
 * is an affordance for producing a credential, not a role switch.
 *
 * The camera preview is running the whole time on purpose: waiting is the one
 * moment in a call when somebody has time to notice their microphone is the
 * wrong one, and this screen exists precisely to create that moment.
 */

import * as React from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Loader2, ShieldCheck, Sparkles, Users } from 'lucide-react'
import type { JoinGrant, LobbyView } from '@era/shared'
import { Card, CardContent } from '@/components/ui/card'
import { Field, Input } from '@/components/ui/input'
import { MediaPreview } from '@/features/call/components/media-preview'
import { usePreview } from '@/features/call/use-preview'
import { ApiError, api } from '@/lib/api'
import {
  readHostSession,
  readHostToken,
  recallName,
  rememberName,
  stashGrant,
  stashHostSession,
} from '@/lib/livekit-handoff'
import { cn } from '@/lib/utils'

/**
 * How often the lobby asks whether the meeting has started.
 *
 * Polling rather than SSE, and that is a considered step backwards from the
 * live-call machinery. The `/api/events` stream is scoped to sessions, and the
 * entire point of this screen is the window in which there IS no session — so
 * there is nothing to subscribe to until the moment the answer changes. Three
 * seconds is under the threshold where a waiting person starts wondering
 * whether the page is broken, and a lobby read touches only SQLite.
 */
const POLL_MS = 3_000

/**
 * How many times the lobby will let someone in automatically before it stops
 * and waits for a human.
 *
 * Three is enough to ride out a room that is a moment from being joinable and
 * small enough that a room which will never admit this guest costs three
 * requests rather than an open-ended spin.
 */
const AUTO_JOIN_ATTEMPTS = 3

export function LobbyRoute() {
  const { slug } = useParams({ from: '/j/$slug' })
  const navigate = useNavigate()
  const preview = usePreview()

  const [name, setName] = React.useState(recallName)
  const [busy, setBusy] = React.useState(false)
  const [failure, setFailure] = React.useState<string | null>(null)

  /*
   * Two ways to be the host, and the page treats them identically once either
   * holds — but they are obtained very differently.
   *
   *   the LINK token   this tab created the appointment and holds a capability
   *                    bound to this one meeting.
   *   the PASSWORD     someone signed in with the shared host password, which
   *                    authorizes any meeting in this deployment.
   *
   * The link token is preferred when both exist: it is the narrower authority,
   * and preferring the narrower one means the shared password is used only
   * when it is actually needed.
   */
  const [hostSession, setHostSession] = React.useState<string | null>(readHostSession)
  const linkToken = React.useMemo(() => readHostToken(slug), [slug])
  const hostToken = linkToken ?? hostSession
  const isHost = hostToken !== null

  // Whether this deployment accepts a host password at all. Absent means the
  // sign-in affordance is never rendered, rather than rendered and broken.
  const hostSignIn = useQuery({
    queryKey: ['host-signin'],
    queryFn: () => api<{ enabled: boolean }>('/api/host/session'),
    staleTime: 5 * 60_000,
    retry: false,
  })

  const lobby = useQuery({
    queryKey: ['lobby', slug],
    queryFn: () => api<LobbyView>(`/api/appointments/${slug}`),
    refetchInterval: POLL_MS,
    // Keep asking while the tab is in the background: the common case is
    // somebody opening the link early and switching away until it starts.
    refetchIntervalInBackground: true,
    retry: false,
  })

  const trimmedName = name.trim()
  const view = lobby.data ?? null

  /** Both paths end identically to `/`'s: stash, hand the camera over, go. */
  const enter = React.useCallback(
    (grant: JoinGrant) => {
      rememberName(trimmedName)
      stashGrant({ grant })
      preview.handOff()
      void navigate({ to: '/call/$code', params: { code: grant.join_code } })
    },
    [navigate, preview, trimmedName],
  )

  const start = React.useCallback(async () => {
    if (!hostToken || !trimmedName) return
    setBusy(true)
    setFailure(null)
    try {
      const grant = await api<JoinGrant>(`/api/appointments/${slug}/start`, {
        method: 'POST',
        body: { display_name: trimmedName },
        // The capability goes in the Authorization header and nowhere else.
        bearerToken: hostToken,
      })
      enter(grant)
    } catch (error) {
      const api_ = error instanceof ApiError ? error : null
      setFailure(
        api_?.message ?? 'The meeting could not be started. Try again in a moment.',
      )
      setBusy(false)
      void lobby.refetch()
    }
  }, [enter, hostToken, lobby, slug, trimmedName])

  const join = React.useCallback(async () => {
    if (!trimmedName) return
    setBusy(true)
    setFailure(null)
    try {
      const grant = await api<JoinGrant>(`/api/appointments/${slug}/join`, {
        method: 'POST',
        body: { display_name: trimmedName },
      })
      enter(grant)
    } catch (error) {
      const api_ = error instanceof ApiError ? error : null
      // `lobby_waiting` is not a failure — it is the host not having started
      // yet, which is the normal state of this screen. Say nothing and let the
      // poll pick the change up.
      if (api_?.code !== 'lobby_waiting') {
        setFailure(api_?.message ?? 'Could not join. Try again in a moment.')
        // A real refusal — the room is full, closed, or will not have us.
        // Stop trying by ourselves; the button is still there if they want it.
        armed.current = false
      }
      setBusy(false)
    }
  }, [enter, slug, trimmedName])

  /*
   * "Waiting attendees are released automatically" (handoff §3).
   *
   * A guest who has already typed their name is let in the instant the meeting
   * goes live, with no second click — the click they would have made is the
   * one they already made when they entered their name and pressed the button.
   * Guarded on `busy` so the effect cannot fire a second join while the first
   * is in flight, and on `isHost` because the host arrives through `start`.
   *
   * THE ATTEMPT CAP IS NOT DEFENSIVE PADDING. Without it this is an unbounded
   * retry loop: `join()` clears `busy` in its catch, which re-satisfies this
   * effect's guard, which calls `join()` again — with no delay, forever, and
   * every attempt can reach the upstream token mint. A room that is joinable
   * but refuses this guest (participant limit reached, the quality-grant
   * ceiling, a room closing as they arrive) turns one waiting browser into a
   * spin against the control plane for as long as the tab is open.
   *
   * So: a small number of automatic attempts, and then stop and let the person
   * decide. `lobby_waiting` is not counted — it is the normal answer while the
   * host has not started, and `join()` does not surface it as a failure.
   */
  const armed = React.useRef(false)
  const autoAttempts = React.useRef(0)
  React.useEffect(() => {
    if (!view?.joinable || isHost || busy || !armed.current) return
    if (autoAttempts.current >= AUTO_JOIN_ATTEMPTS) {
      // Disarm rather than keep firing. The button is still there.
      armed.current = false
      return
    }
    autoAttempts.current += 1
    void join()
  }, [busy, isHost, join, view?.joinable])

  if (lobby.isError) {
    const error = lobby.error instanceof ApiError ? lobby.error : null
    return (
      <Empty
        title={
          // A malformed slug is a 400, not a 404, and fell to the generic
          // branch — so a link that lost a character in a chat message was
          // reported as a server fault, over the raw lowercased slug. It is
          // the most likely failure on this screen and deserves its own
          // sentence.
          error?.status === 400 || error?.status === 404
            ? 'That meeting link does not work.'
            : 'Something went wrong.'
        }
        detail={
          error?.status === 400
            ? 'The link looks incomplete — it may have lost a character on the way. Ask whoever sent it for the full link.'
            : error?.status === 404
              ? 'The link may have a typo, or the meeting may have been removed.'
              : (error?.message ?? 'The server is not responding.')
        }
      />
    )
  }

  const state = view?.state ?? 'lobby'
  const starting = state === 'starting'

  return (
    /* The phone budget, measured on an iPhone SE (667px): the name field and
       the button that acts on it must both be above the fold, because a guest
       who cannot see the control does not know the screen is waiting for them
       and there is no scroll cue telling them to look.
       
       A long meeting title was enough to push the button 19px under. Three
       things pay for it below `sm`, none of which removes information:
       the eyebrow is dropped (the title and the host line already say what
       this is), the title is clamped to two lines with the full text still in
       the accessible name and the tooltip, and the gaps tighten. At `sm` and
       up nothing changes — there is room, and the page is allowed to breathe. */
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 py-3 sm:gap-5 sm:py-8">
      <header className="space-y-1 text-center sm:space-y-1.5">
        <p className="eyebrow hidden sm:block">{isHost ? 'You are the host' : 'Meeting'}</p>
        <h1
          className="display line-clamp-2 text-xl sm:text-3xl"
          title={view?.title ?? undefined}
        >
          {view?.title ?? 'Waiting room'}
        </h1>
        <p className="text-sm text-ink-muted">
          {isHost
            ? 'Nothing is created until you start it.'
            : `${view?.host_name ?? 'The host'} will let everyone in.`}
        </p>
      </header>

      <MediaPreview preview={preview} displayName={trimmedName} />

      <Card>
        <CardContent className="space-y-3 p-3.5 sm:space-y-4 sm:p-5">
          <Field label="Your name" htmlFor="lobby-name">
            <Input
              id="lobby-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="What should people call you?"
              maxLength={60}
              autoComplete="name"
              className="h-11 sm:h-12"
            />
          </Field>

          <StatusLine host={isHost} state={state} hostName={view?.host_name ?? null} />

          {/* A failed start, stated honestly in both directions.
              `retryable` is the controller's own classification, forwarded by
              the BFF: a capacity refusal resolves itself and a
              `profile_not_accepted` never will. Telling someone to "try again"
              about the second one sends them into a loop that cannot end —
              which is exactly what this screen did before the failure
              classification was fixed. The detail is a fixed server-side
              sentence, never the upstream message. */}
          {view?.last_error ? (
            <p
              className={cn(
                'rounded-md border px-3 py-2 text-xs leading-relaxed',
                view.last_error.retryable
                  ? 'border-warn/40 bg-warn/5 text-ink-muted'
                  : 'border-danger/40 bg-danger/5 text-ink',
              )}
            >
              The meeting could not be started: {view.last_error.detail}. Nobody has been let in
              and nothing is being recorded.
              {view.last_error.retryable
                ? isHost
                  ? ' Try again.'
                  : ' The host can try again.'
                : isHost
                  ? ' Retrying will not help — create a new link with different settings.'
                  : ' Retrying will not help; ask the host for a new link.'}
            </p>
          ) : null}

          {failure ? (
            <p className="rounded-md border border-danger/40 bg-danger/5 px-3 py-2 text-xs leading-relaxed text-ink">
              {failure}
            </p>
          ) : null}

          <button
            type="button"
            disabled={busy || starting || !trimmedName}
            onClick={() => {
              armed.current = true
              if (isHost) void start()
              else void join()
            }}
            className={cn(
              'inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-medium',
              'bg-accent text-accent-fg shadow-sm transition-[filter,opacity] hover:brightness-110',
              'active:brightness-95 disabled:pointer-events-none disabled:opacity-45',
            )}
          >
            {busy || starting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                {isHost ? 'Starting the meeting…' : 'Joining…'}
              </>
            ) : isHost ? (
              <>
                <Sparkles className="size-4" />
                Start the meeting
                <ArrowRight className="size-4" />
              </>
            ) : view?.joinable ? (
              <>
                <Users className="size-4" />
                Join now
                <ArrowRight className="size-4" />
              </>
            ) : (
              <>
                <Users className="size-4" />
                Wait for {view?.host_name ?? 'the host'}
              </>
            )}
          </button>

          {!isHost && !view?.joinable ? (
            <p className="text-center text-xs text-ink-dim">
              Enter your name now and you will be let in the moment it starts.
            </p>
          ) : null}

          {/* The host sign-in, shown only to people who are not already the
              host and only where the deployment accepts a password at all.
              Collapsed behind a text button because the overwhelming majority
              of people opening a meeting link are guests, and a password field
              presented to a guest is a question they cannot answer. */}
          {!isHost && hostSignIn.data?.enabled ? (
            <HostSignIn
              onSignedIn={(token) => {
                setHostSession(token)
                setFailure(null)
                void lobby.refetch()
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 px-1 text-xs leading-relaxed text-ink-dim">
        <ShieldCheck aria-hidden className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Nothing is recorded, and no call exists, until the host starts this meeting. What is
          kept once it starts is shown in the call itself.
        </span>
      </p>
    </div>
  )
}

/**
 * Exchange the shared host password for a session token.
 *
 * The password is sent once, to one endpoint, and is never stored — what comes
 * back is a signed token with an expiry, and that is what the browser keeps.
 * A wrong password is reported as one flat failure with no hint about which
 * part was wrong, matching what the server says.
 */
function HostSignIn({ onSignedIn }: { onSignedIn: (token: string) => void }) {
  const [open, setOpen] = React.useState(false)
  const [password, setPassword] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const submit = React.useCallback(async () => {
    if (!password) return
    setBusy(true)
    setError(null)
    try {
      const session = await api<{ token: string; expires_at: string }>('/api/host/session', {
        method: 'POST',
        body: { password },
      })
      stashHostSession(session)
      // Drop the password from React state the moment it has been exchanged.
      setPassword('')
      setOpen(false)
      onSignedIn(session.token)
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.status === 401
          ? 'That is not the host password.'
          : 'Could not sign in. Try again in a moment.',
      )
    } finally {
      setBusy(false)
    }
  }, [onSignedIn, password])

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mx-auto block text-xs text-ink-muted underline underline-offset-4 transition-colors hover:text-ink"
      >
        I’m the host
      </button>
    )
  }

  return (
    <div className="space-y-2 rounded-md border border-line bg-surface-sunken/50 p-3">
      <Field label="Host password" htmlFor="host-password">
        <Input
          id="host-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void submit()
            }
          }}
          placeholder="••••••••"
          autoComplete="current-password"
          maxLength={256}
          autoFocus
          className="h-11"
        />
      </Field>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={busy || !password}
          onClick={() => void submit()}
          className={cn(
            'inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-md text-sm font-medium',
            'bg-ink text-surface transition-[filter,opacity] hover:brightness-110',
            'disabled:pointer-events-none disabled:opacity-45',
          )}
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : null}
          Sign in as host
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false)
            setPassword('')
            setError(null)
          }}
          className="h-10 rounded-md px-3 text-sm text-ink-muted transition-colors hover:text-ink"
        >
          Cancel
        </button>
      </div>
      <p className="text-xs leading-relaxed text-ink-dim">
        Only needed if you are hosting from a different device than the one that
        created the link.
      </p>
    </div>
  )
}

function StatusLine({
  host,
  state,
  hostName,
}: {
  host: boolean
  state: LobbyView['state']
  hostName: string | null
}) {
  if (state === 'active') {
    return (
      <p className="flex items-center gap-2 text-sm text-ok">
        <span className="size-1.5 rounded-full bg-ok" aria-hidden />
        The meeting is live.
      </p>
    )
  }
  if (state === 'starting') {
    return (
      <p className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 aria-hidden className="size-3.5 animate-spin" />
        {host ? 'Creating the room…' : `${hostName ?? 'The host'} is starting the meeting…`}
      </p>
    )
  }
  return (
    <p className="flex items-center gap-2 text-sm text-ink-muted">
      <span className="size-1.5 rounded-full bg-ink-dim" aria-hidden />
      {host
        ? 'Not started. No room exists yet.'
        : `Waiting for ${hostName ?? 'the host'} to start.`}
    </p>
  )
}

function Empty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="mx-auto max-w-lg space-y-3 py-20 text-center">
      <h1 className="display text-2xl">{title}</h1>
      <p className="text-sm text-ink-muted">{detail}</p>
    </div>
  )
}
